import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEvents } from '../../context/EventsContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { ArrowLeft, Save, Loader2, ImageIcon, Check, Upload, X } from 'lucide-react';

const EventForm = () => {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const { events, createEvent, updateEvent, getPaymentMethods, getEventDetail } = useEvents();
  const isEditing = !!eventId;
  
  const [isLoading, setIsLoading] = useState(false);
  const [allPMs, setAllPMs] = useState([]);
  const [selectedPMs, setSelectedPMs] = useState([]);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    date: '',
    time: '',
    image: '',
    currency: 'USD',
    status: 'open'
  });

  useEffect(() => {
    const load = async () => {
      const pms = await getPaymentMethods();
      setAllPMs(pms || []);
      if (isEditing) {
        const event = await getEventDetail(eventId);
        if (event) {
          setFormData({
            name: event.name,
            description: event.description,
            date: event.date,
            time: event.time,
            image: event.image || '',
            currency: event.currency,
            status: event.status
          });
          if (event.image) setImagePreview(event.image);
          setSelectedPMs(event.paymentMethodIds || []);
        }
      }
    };
    load();
  }, [eventId, isEditing, getEventDetail, getPaymentMethods]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const fd = new FormData();
      fd.append('name', formData.name);
      fd.append('description', formData.description);
      fd.append('date', formData.date);
      fd.append('time', formData.time);
      fd.append('currency', formData.currency);
      fd.append('status', formData.status);
      fd.append('payment_method_ids', selectedPMs.join(','));
      if (imageFile) {
        fd.append('image', imageFile);
      } else if (formData.image) {
        fd.append('image_url', formData.image);
      }

      if (isEditing) {
        await updateEvent(eventId, fd);
      } else {
        const newEvent = await createEvent(fd);
        navigate(`/admin/events/${newEvent.id}/layout`);
        return;
      }
      navigate('/admin/dashboard');
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const sampleImages = [
    'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&q=80&w=800',
    'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&q=80&w=800',
    'https://images.unsplash.com/photo-1504609773096-104ff2c73ba4?auto=format&fit=crop&q=80&w=800',
    'https://images.unsplash.com/photo-1559339352-11d035aa65de?auto=format&fit=crop&q=80&w=800',
    'https://images.unsplash.com/photo-1470337458703-46ad1756a187?auto=format&fit=crop&q=80&w=800',
    'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&q=80&w=800'
  ];

  return (
    <div data-testid="event-form-page" className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="border-b border-border bg-secondary/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-3 sm:gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/dashboard')}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Volver</span>
          </Button>
          <h1 className="text-base sm:text-xl font-serif font-semibold text-foreground truncate">
            {isEditing ? 'Editar Evento' : 'Nuevo Evento'}
          </h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info */}
          <div className="bg-secondary border border-border rounded-lg p-6 space-y-6">
            <h2 className="text-lg font-medium text-foreground">Información Básica</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre del Evento *</Label>
                <Input
                  id="name"
                  data-testid="event-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ej: Noche de Jazz & Vino"
                  className="bg-background border-border"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="currency">Moneda</Label>
                <Select
                  value={formData.currency}
                  onValueChange={(value) => setFormData({ ...formData, currency: value })}
                >
                  <SelectTrigger className="bg-background border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD - Dólar</SelectItem>
                    <SelectItem value="EUR">EUR - Euro</SelectItem>
                    <SelectItem value="MXN">MXN - Peso Mexicano</SelectItem>
                    <SelectItem value="COP">COP - Peso Colombiano</SelectItem>
                    <SelectItem value="ARS">ARS - Peso Argentino</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descripción *</Label>
              <Textarea
                id="description"
                data-testid="event-description-input"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe el evento..."
                className="bg-background border-border min-h-[100px]"
                required
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="date">Fecha *</Label>
                <Input
                  id="date"
                  type="date"
                  data-testid="event-date-input"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="bg-background border-border"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="time">Hora *</Label>
                <Input
                  id="time"
                  type="time"
                  data-testid="event-time-input"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  className="bg-background border-border"
                  required
                />
              </div>
            </div>
          </div>

          {/* Image */}
          <div className="bg-secondary border border-border rounded-lg p-6 space-y-6">
            <h2 className="text-lg font-medium text-foreground">Imagen del Evento</h2>
            
            {/* File Upload */}
            <div className="space-y-2">
              <Label>Subir imagen</Label>
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
              >
                {imagePreview ? (
                  <div className="relative">
                    <img src={imagePreview} alt="Preview" className="w-full h-40 object-cover rounded" />
                    <button type="button" onClick={(e) => { e.stopPropagation(); setImageFile(null); setImagePreview(null); }} className="absolute top-2 right-2 p-1 bg-black/60 rounded-full hover:bg-black/80">
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">Click para subir imagen</p>
                    <p className="text-xs text-muted-foreground/60">PNG, JPG hasta 10MB</p>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setImageFile(file);
                    setImagePreview(URL.createObjectURL(file));
                    setFormData(prev => ({ ...prev, image: '' }));
                  }
                }}
              />
            </div>

            {/* OR URL */}
            {!imageFile && (
              <>
                <div className="flex items-center gap-3">
                  <div className="flex-1 border-t border-border" />
                  <span className="text-xs text-muted-foreground">o usa una URL</span>
                  <div className="flex-1 border-t border-border" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="image">URL de la Imagen</Label>
                  <Input
                    id="image"
                    data-testid="event-image-input"
                    value={formData.image}
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                    placeholder="https://..."
                    className="bg-background border-border"
                  />
                </div>

                {formData.image && (
                  <div className="relative w-full h-48 rounded-lg overflow-hidden border border-border">
                    <img src={formData.image} alt="Preview" className="w-full h-full object-cover" onError={(e) => e.target.style.display = 'none'} />
                  </div>
                )}

                <div>
                  <p className="text-sm text-muted-foreground mb-3">O selecciona una imagen de muestra:</p>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                    {sampleImages.map((img) => (
                      <button key={img} type="button" onClick={() => setFormData({ ...formData, image: img })}
                        className={`relative aspect-video rounded overflow-hidden border-2 transition-all ${formData.image === img ? 'border-primary' : 'border-transparent hover:border-primary/50'}`}>
                        <img src={img} alt="Sample" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Payment Methods Selection */}
          <div className="bg-secondary border border-border rounded-lg p-6 space-y-4">
            <div>
              <h3 className="font-medium text-foreground">Métodos de Pago</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Selecciona qué métodos de pago estarán disponibles para este evento</p>
            </div>
            {allPMs.length === 0 ? (
              <p className="text-sm text-muted-foreground">No hay métodos de pago configurados. Créalos desde el panel de Métodos de Pago.</p>
            ) : (
              <div className="space-y-2">
                {allPMs.map(pm => {
                  const isSelected = selectedPMs.includes(pm.id);
                  return (
                    <button
                      key={pm.id}
                      type="button"
                      onClick={() => {
                        setSelectedPMs(prev =>
                          isSelected ? prev.filter(id => id !== pm.id) : [...prev, pm.id]
                        );
                      }}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left text-sm
                        ${isSelected
                          ? 'border-primary bg-primary/10 text-foreground'
                          : 'border-border bg-background text-muted-foreground hover:border-primary/40'
                        }`}
                    >
                      <div className={`w-5 h-5 rounded flex items-center justify-center border ${isSelected ? 'bg-primary border-primary' : 'border-border'}`}>
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="font-medium">{pm.name}</span>
                        {!pm.active && <span className="ml-2 text-xs text-zinc-500">(Inactivo)</span>}
                      </div>
                      {pm.bankInfo && <span className="text-xs text-muted-foreground">{pm.bankInfo.banco}</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin/dashboard')}
              className="border-border"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              data-testid="save-event-btn"
              className="bg-primary hover:bg-accent"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isEditing ? 'Guardar Cambios' : 'Crear y Diseñar Layout'}
                </>
              )}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default EventForm;
